/**
 * API client — spec §D (endpoints) and §F1 (server state / query keys).
 *
 * Everything goes through the same origin: nginx (§B3) serves the SPA and proxies
 * `/api/` to Django, so the base URL is a path, not a host. In `vite dev` the same
 * path is proxied to the compose stack (see vite.config.ts).
 */
import { QueryClient, type QueryFunction } from '@tanstack/react-query';
import { currentLanguage, DEFAULT_LANGUAGE, type Language } from './i18n';
import {
  isOverlayLevel,
  type Country,
  type ReportingLevel,
  type Role,
  type SessionUser,
} from './state';

export const API_BASE = '/api/v1';

/**
 * `?lang=` for the three localised catalogue endpoints — empty for English.
 *
 * English is the server's default and its fallback, so the parameter would be a no-op: an
 * omitted `lang=en` keeps every English URL, cache entry and access-log line exactly as it
 * was before French existed.
 */
export const langParam = (lang: Language): string =>
  lang === DEFAULT_LANGUAGE ? '' : `?lang=${encodeURIComponent(lang)}`;

/**
 * The three localised query keys, named so the fetchers can *take the language off the key
 * they were invoked under* rather than re-derive it.
 *
 * That is the whole trick that keeps this change small and correct: the fetcher is a
 * `QueryFunction`, TanStack hands it the key it is fetching for, and the URL is built from
 * that key's language. The key and the request can therefore never disagree — there is no
 * second read of `currentLanguage()` to be a tick out of date — and no call site has to
 * thread a language through, so `queryFn: fetchIndicators` still reads exactly as it did.
 */
export type ConfigKey = readonly ['config', Language];
export type IndicatorsKey = readonly ['indicators', Language];
export type LayersKey = readonly ['layers', Role, Language];

/** §D error envelope: `{"error": {"code": ..., "message": ..., "details": ...}}`. */
export interface ApiErrorBody {
  error?: { code?: string; message?: string; details?: unknown };
}

export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details: unknown;
  readonly requestId: string | null;

  constructor(status: number, code: string, message: string, details: unknown, requestId: string | null) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
    this.requestId = requestId;
  }
}

function qs(params: Record<string, string | number | boolean | null | undefined>): string {
  const sp = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === null || v === undefined || v === '') continue;
    sp.set(k, String(v));
  }
  const s = sp.toString();
  return s ? `?${s}` : '';
}

/**
 * Typed GET against `/api/v1`. Throws `ApiError` carrying the §D envelope's code and
 * the `X-Request-Id` header (§D/§J4) so failures are traceable back to the api log.
 */
export async function getJson<T>(path: string, init?: RequestInit): Promise<T> {
  const url = path.startsWith('http') ? path : `${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`;
  const res = await fetch(url, {
    ...init,
    headers: { Accept: 'application/json', ...(init?.headers ?? {}) },
  });
  const requestId = res.headers.get('X-Request-Id');
  if (!res.ok) {
    let body: ApiErrorBody = {};
    try {
      body = (await res.json()) as ApiErrorBody;
    } catch {
      /* non-JSON error page (e.g. nginx 502) — fall through to the status text */
    }
    throw new ApiError(
      res.status,
      body.error?.code ?? `http_${res.status}`,
      body.error?.message ?? res.statusText ?? 'Request failed',
      body.error?.details ?? null,
      requestId,
    );
  }
  return (await res.json()) as T;
}

/* ------------------------------------------------------- §D1/§I4 session & CSRF */

/**
 * Django's CSRF cookie. Deliberately *not* HttpOnly (settings/base.py) — DRF's
 * `SessionAuthentication` enforces CSRF on every unsafe method of a live session, and
 * the SPA is a static bundle with no server-rendered template to seed the token from,
 * so it has to read the cookie and echo it in `X-CSRFToken`.
 */
export const CSRF_COOKIE = 'csrftoken';
export const CSRF_HEADER = 'X-CSRFToken';

export function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const entry = part.trim();
    if (entry.startsWith(prefix)) return decodeURIComponent(entry.slice(prefix.length));
  }
  return null;
}

/** In-flight `/auth/csrf` request, shared by concurrent callers (see `ensureCsrf`). */
let csrfRequest: Promise<string | null> | null = null;

/**
 * Make sure a CSRF token exists before the first non-GET of the session.
 * `GET /auth/csrf` is a 204 whose only job is `Set-Cookie: csrftoken=…`; once the
 * cookie is there this is free, so callers can invoke it unconditionally.
 *
 * Concurrent callers share one request: two mutations firing on the same tick would
 * otherwise each seed a token, and the second `Set-Cookie` would replace the secret the
 * first one had already read into its header — a spurious CSRF failure that only shows
 * up under load.
 */
export async function ensureCsrf(): Promise<string | null> {
  const existing = readCookie(CSRF_COOKIE);
  if (existing) return existing;
  if (!csrfRequest) {
    csrfRequest = fetch(`${API_BASE}/auth/csrf`, { credentials: 'same-origin' })
      .then(() => readCookie(CSRF_COOKIE))
      .finally(() => {
        csrfRequest = null;
      });
  }
  return csrfRequest;
}

/** Typed non-GET against `/api/v1`, carrying the §I4 CSRF token. `null` body → no body. */
export async function sendJson<T>(
  path: string,
  body: unknown,
  method: 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST',
): Promise<T | null> {
  const token = await ensureCsrf();
  const res = await fetch(`${API_BASE}${path.startsWith('/') ? '' : '/'}${path}`, {
    method,
    credentials: 'same-origin',
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      ...(token ? { [CSRF_HEADER]: token } : {}),
    },
    ...(body === undefined ? {} : { body: JSON.stringify(body) }),
  });
  const requestId = res.headers.get('X-Request-Id');
  if (!res.ok) {
    let envelope: ApiErrorBody = {};
    try {
      envelope = (await res.json()) as ApiErrorBody;
    } catch {
      /* non-JSON error page — fall through to the status text */
    }
    throw new ApiError(
      res.status,
      envelope.error?.code ?? `http_${res.status}`,
      envelope.error?.message ?? res.statusText ?? 'Request failed',
      envelope.error?.details ?? null,
      requestId,
    );
  }
  if (res.status === 204) return null;
  return (await res.json()) as T;
}

/** §D1 `{id,name,role,org,country_scope}` — defined next to `Role`, in `lib/state.ts`. */
export type { SessionUser };

export interface Session {
  user: SessionUser;
}

/** §D1 `GET /auth/me` — the boot call that decides the SPA's §I2 gating. */
export const fetchMe = () => getJson<Session>('/auth/me', { credentials: 'same-origin' });

/** §D1 `POST /auth/login`. Throws `ApiError` (401 `unauthenticated`, 429 `rate_limited`). */
export const login = (username: string, password: string) =>
  sendJson<Session>('/auth/login', { username, password }).then((s) => s as Session);

/** §D1 `POST /auth/logout` — 204, and the server drops the session cookie. */
export const logout = () => sendJson<null>('/auth/logout', undefined).then(() => undefined);

/* ------------------------------------------------------------------ §D1 meta */

export interface Domain {
  id: string;
  name: string;
  short_name: string;
  color: string;
  icon: string;
}

export type AccessLevel = 'full' | 'limited' | 'none';

/**
 * One §F5.1 basemap, as `/meta/config.basemaps` serves it.
 *
 * `maxZoom` is camelCase where the rest of this payload is snake_case, deliberately: it is
 * a Leaflet `TileLayer` option name and is passed straight through to `L.tileLayer`, so
 * renaming it on the wire would only mean renaming it back on arrival.
 *
 * The `key` is the §F1 `basemap` code the permalink's `bm=` carries; `label` is prose and is
 * translated per language like the rest of the payload.
 */
export interface BasemapConfig {
  key: string;
  label: string;
  url: string;
  attribution: string;
  maxZoom: number;
}

/**
 * The §F5.1 catalogue and the cold-boot basemap that goes with it.
 *
 * `default` names one of `list`'s keys — the deployment's own starting basemap, `light`
 * where CARTO tiles are configured and `streets` where they are not. It is served *with* the
 * list rather than beside it because it is a property of the list, and the two must not be
 * able to disagree.
 */
export interface BasemapsConfig {
  default: string;
  list: BasemapConfig[];
}

/**
 * One theme's row of S3's default table (`apps/meta/themes.py`), as `/meta/config` serves it.
 *
 * The **client's own table**, resolved server-side against the catalogue: `layers` never
 * names a row `/layers` does not carry, and the six flags are the departures from "an
 * ordinary theme with one default dataset". The explorer branches on the flags rather than
 * on the theme key, so a client revision is a server change and not a rebuild of this
 * bundle.
 */
export interface ThemeDefault {
  theme: string;
  /** §D5 layer ids, catalogue order — the theme's default datasets. */
  layers: string[];
  /**
   * §C indicator ids the theme opens on. Only the MELS **baseline variables** appear here
   * (GESI's extension pair, SLM's twelve crops): S3 moves contractual and thematic
   * indicators behind the Indicators icon, and a baseline variable is a survey measurement
   * the themes present as a dataset. See `DatasetsPane`.
   */
  indicators: string[];
  /** `rasters.EPOCH_KINDS` family a time slider should step through, or null. */
  series: string | null;
  /**
   * §D5 ids this theme's dataset selects must **not** list — the 9 Sep 2026 one-epoch switch
   * (`apps/meta/themes.py::HIDDEN_LAYERS`). Empty for every theme but Land Use, whose six are
   * the other land-cover rows a reader could mistake for the 2024 one. Serving-level only:
   * the rows are still in `/layers` and still reachable by every other surface.
   */
  hidden_layers?: string[];
  /** cadence → layer id, for a theme published at more than one interval (rainfall). */
  cadences: Record<string, string>;
  provisional: boolean;
  in_preparation: boolean;
  requires_signin: boolean;
  display_only: boolean;
  excludes_indicators: boolean;
  contractual_only: boolean;
  /** True for a **2025 MELS baseline sheet** rather than a §C1 domain — see `BaselineTheme`. */
  baseline: boolean;
  /** The workbook sheet behind a baseline theme (`apps/indicators/groups.py`), else null. */
  group_id: string | null;
  /** The client's own sentence about this theme's state, translated. */
  note: string | null;
}

/**
 * One 2025 MELS baseline sheet, as its own rail entry — client directive, 8 Sep 2026.
 *
 * The same row is mirrored into `themes` under `key`, so a baseline chip is an ordinary
 * theme everywhere below the rail. What lives only here is the pair of names the rail needs
 * and no other theme has: `label` is the sheet's full name (the accessible name and the
 * tooltip) and `short` is the word the chip prints.
 */
export interface BaselineTheme {
  /** `bl_<group_id>` — the key in `order` and in `themes`. */
  key: string;
  group_id: string;
  label: string;
  short: string;
  /**
   * The two-letter mark the chip prints where a domain prints its icon. **Not translated** —
   * it stands in for an icon, and an icon does not change with the interface language. Null
   * for a sheet the server has no mark for, and then the SPA derives one (`monogramOf`).
   */
  monogram: string | null;
  /** The sheet's member ids, in the MEL team's own block order. */
  indicators: string[];
  /** The variable the chip opens on — the sheet's first member. */
  default: string;
}

/** S3's reference layers — the outlines every theme draws over. */
export interface ReferenceLayerConfig {
  key: string;
  unit_type: UnitType;
  admin_level: number | null;
  default_on: boolean;
  /** True for a row that appears **only** once a filter is applied (watersheds, partnerships). */
  filter_only: boolean;
}

/** `/meta/config.theme_defaults` — the rail's order, the table, and the reference layers. */
export interface ThemeDefaultsConfig {
  /**
   * Rail order: All, the eight domains, the 2025 baseline sheets, Indicators last.
   *
   * The client's S3 clause puts the Indicators icon "after Digital Agriculture"; the 8 Sep
   * directive puts the baseline band between the two, so the icon is still last and the
   * baseline group is what now follows Digital Agriculture.
   */
  order: string[];
  themes: Record<string, ThemeDefault>;
  /** The baseline sheets, in order — the rail's third kind of entry. */
  baseline_themes?: BaselineTheme[];
  /**
   * Series layer ids whose **time slider is off** (`apps/meta/themes.py::LOCKED_SERIES`).
   *
   * The 9 Sep 2026 directive: Land Use shows the 2024 land cover and nothing else. The lock
   * is on the series rather than on the theme because a slider is a property of the drawn
   * dataset — locking it on Land Use alone would leave the same eight years steppable from
   * All. The `epochs` payload is untouched, so the per-year rows stay out of the datasets
   * list exactly as before.
   */
  locked_series?: string[];
  reference_layers: ReferenceLayerConfig[];
}

/**
 * One §I1 role with the client's persona display name (`apps/accounts/personas.py`).
 *
 * `role` is the code every enforcement path speaks; `label` and `members` are copy. Two
 * roles deliberately share a `label` — `members` is what tells them apart in a list.
 */
export interface Persona {
  role: Role;
  label: string;
  members: string[];
}

/** A persona the client named that no §I1 role grants — display only. */
export interface ReservedPersona {
  key: string;
  label: string;
  members: string[];
  reserved_for: string;
}

export interface MetaConfig {
  domains: Domain[];
  years: number[];
  countries: Record<string, string>;
  ramps: Record<string, string[]>;
  biv: string[][];
  access_matrix: Record<string, Record<string, AccessLevel>>;
  features: Record<string, boolean>;
  /**
   * §F5.1, server-owned since the CARTO key round. **Optional on the type, always present
   * on a current server**: `/meta/config` is memoised for five minutes and cached in the
   * browser for the same, so a client can hold a payload from before the block existed —
   * and `basemapsFromConfig` falls back to the built-in list for exactly that window.
   */
  basemaps?: BasemapsConfig;
  /**
   * S3 phase A. Optional for the same reason `basemaps` is — a cached payload from before
   * the block existed — and every reader of it (`themes.ts`) treats absence as "no theme
   * table yet" rather than as an empty one.
   */
  theme_defaults?: ThemeDefaultsConfig;
  /** S3's persona display names. See `Persona`; the machinery is unchanged. */
  personas?: Persona[];
  reserved_personas?: ReservedPersona[];
  access_matrix_labels?: {
    roles: Record<string, string>;
    classifications: Record<string, string>;
    levels: Record<string, string>;
    limited_rules: Record<string, string>;
  };
}

/**
 * §D1 `GET /meta/config[?lang=fr]`.
 *
 * **A query parameter, not `Accept-Language`.** The server honours both, but a header makes
 * the same URL mean two things, which every shared cache in front of Django then has to be
 * taught about with a `Vary` — and nginx's §B3 `/api/` location is a plain `proxy_pass` with
 * no `proxy_cache`, so a mis-keyed entry would be a silent, invisible-in-development way of
 * serving French to an English reader. One URL per answer keeps that impossible.
 *
 * `lang=en` is **omitted**: English is the server's default, so the English URL stays
 * byte-for-byte what it has always been (the same rule `fetchValues` applies to `mode`), and
 * nothing about an English session changes because French exists.
 */
export const fetchConfig: QueryFunction<MetaConfig, ConfigKey> = ({ queryKey: [, lang] }) =>
  getJson<MetaConfig>(`/meta/config${langParam(lang)}`);

/**
 * §D1 `/stats/overview`. **The keys are §D1's; what they count is a MELS activity.**
 *
 * `sites_by_type` is the activity-status donut (`type` is `realized`/`in_progress`/
 * `upcoming`, drafts excluded) and `operation_points` is the number of *located* ones —
 * i.e. the number of activities the mini-map's markers add up to. The names did not change
 * with the meaning because §D1 fixes them and this type is the contract with the server;
 * see `apps/meta/views.py::stats_overview` for the whole reasoning.
 */
export interface StatsOverview {
  /** Activities with a resolved location — §F4's first stat tile. */
  operation_points: number;
  /** Distinct org units carrying ≥1 activity (mostly, but not only, communes). */
  communes_reached: number;
  /** B.2.1 producers reached, cumulative to the current year. */
  farmers_engaged: number;
  datasets: number;
  indicators: number;
  /** Activities by status — the §F4 donut and its legend. Server-translated. */
  sites_by_type: { type: string; label: string; color: string; count: number }[];
  sites_by_country: { country: string; count: number }[];
  /** B.2.1 cumulative, one entry per §F1 year; `null` where a year has no data. */
  headline_trend: { indicator: string; values: (number | null)[] };
}

export type OverviewKey = readonly ['overview', Country, Language];

/**
 * **Language-keyed**, unlike the geometry endpoints: `sites_by_type[].label` is prose the
 * server translates, so the English and the French answers are two different payloads.
 */
export const fetchOverview: QueryFunction<StatsOverview, OverviewKey> = ({
  queryKey: [, country, lang],
}) =>
  getJson<StatsOverview>(
    `/stats/overview${qs({
      country: country === 'ALL' ? null : country,
      lang: lang === DEFAULT_LANGUAGE ? null : lang,
    })}`,
  );

/** §D1 `/audit/ticker` — the §F4 pill's lines, already derived and translated server-side. */
export interface TickerLine {
  text: string;
}

export type TickerKey = readonly ['ticker', Language];

export const fetchTicker: QueryFunction<TickerLine[], TickerKey> = ({ queryKey: [, lang] }) =>
  getJson<TickerLine[]>(`/audit/ticker${langParam(lang)}`);

/**
 * `/activities/points` — one feature per org unit, not per activity (the server aggregates
 * and caps at 200; see `apps/indicators/views.py::activity_points`).
 */
export interface ActivityPointProperties {
  unit_id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  country: string | null;
  count: number;
  /**
   * Modal status of the unit's activities. `null` under `?by=watershed` for a watershed
   * with no activities at all — a choropleth needs a class for zero, so the server sends
   * the feature rather than omitting it.
   */
  dominant_status: string | null;
  by_status: Record<string, number>;
  /** `?by=watershed` only: MELS' own watershed id, the column the roll-up grouped on. */
  watershed_id?: number;
  [key: string]: unknown;
}

/**
 * The grouping `?by=` asks for.
 *
 * `unit` is the default and the historical payload: one feature per org unit the location
 * resolver landed an activity on, which for two thirds of the registry is a *commune*.
 * `watershed` is the R2 roll-up — MELS' own `watershed_id` on every row, commune-located
 * ones included — and is a different question, not a filter on the same answer (NOTES #480).
 */
export type ActivityGrouping = 'unit' | 'watershed';

export type ActivityPointsFC = FeatureCollection<
  { type: 'Point'; coordinates: [number, number] },
  ActivityPointProperties
> & {
  /** Echoed back by the server under `?by=`; absent on the default payload. */
  by?: ActivityGrouping;
  /**
   * `?by=watershed` only: non-draft activities that belong to **no** watershed — MELS'
   * "Tous les bassins versants" pseudo-ids and the handful carrying none. `sum(count) +
   * unassigned` is the scope's whole registry, so a choropleth that does not say this
   * number is quietly short by it.
   */
  unassigned?: number;
};

export const fetchActivityPoints = (country: Country = 'ALL', by: ActivityGrouping = 'unit') =>
  getJson<ActivityPointsFC>(
    `/activities/points${qs({
      country: country === 'ALL' ? null : country,
      // The default is omitted so the unscoped URL is byte-for-byte the one the server has
      // been caching for a week.
      by: by === 'unit' ? null : by,
    })}`,
  );

/* ------------------------------------- S2 Overview: activities by programme pathway */

/** One pathway in the dictionary the server sends with the counts — translated, coloured. */
export interface Pathway {
  id: string;
  label: string;
  color: string;
}

/** The three counts, plus the two denominators that make them honest. */
export interface PathwayBlock {
  counts: Record<string, number>;
  /** Non-draft activities in this row that MELS has never given a category. */
  uncategorised: number;
  categorised: number;
  total: number;
}

export interface PathwayWatershed extends PathwayBlock {
  unit_id: string;
  watershed_id: number | null;
  name: string;
  country: string | null;
}

/**
 * `/activities/by-pathway` — **per watershed**, deliberately.
 *
 * S2's refresh rule recalculates every panel number on every pan, filter and selection.
 * Asking the server for the current scope's three totals would be a request per pan; being
 * handed all eleven basins' three totals once (well under 2 kB) lets the client sum whatever
 * the extent or the selection contains, for free. So this payload is fetched by country and
 * language and by nothing else — the extent is not in the URL, because it is not a question
 * the server is asked.
 */
export interface PathwaysResponse {
  by: 'pathway';
  pathways: Pathway[];
  watersheds: PathwayWatershed[];
  /** Activities belonging to no programme watershed — reported, never folded into a basin. */
  unassigned: PathwayBlock;
  totals: PathwayBlock;
  /** The MELS category → pathway mapping, with live counts. Rendered by nothing; auditable. */
  categories: { id: number; name: string | null; pathway: string; count: number }[];
}

export type PathwaysKey = readonly ['pathways', Country, Language];

export const fetchPathways: QueryFunction<PathwaysResponse, PathwaysKey> = ({
  queryKey: [, country, lang],
}) =>
  getJson<PathwaysResponse>(
    `/activities/by-pathway${qs({
      country: country === 'ALL' ? null : country,
      lang: lang === DEFAULT_LANGUAGE ? null : lang,
    })}`,
  );

/* ---------------------------------------- S2 Overview: Progress Against Targets */

/** One year of IFDC's trajectory. `cumulative_target` is the only figure anything compares
 *  against; `annual_target` is the year's increment and travels because it is in the source. */
export interface TargetRow {
  year: number;
  scope_type: 'programme' | 'country';
  scope_id: string | null;
  annual_target: number;
  cumulative_target: number;
  /** Unit of measure as IFDC delivered it — `Farmers`, `Hectares`. Not an org unit. */
  unit: string;
  /** Where the commitment is written down. A target with no provenance is not loadable. */
  source: string;
  /** `foundation` (2026–2028), `acceleration` (2029–2031), `consolidation` (2032–2033). */
  phase: string | null;
}

/**
 * `/indicators/{id}/targets` — IFDC's cumulative trajectory, delivered 2026-09-09.
 *
 * `pending` is a claim about the **whole delivery**, not about the selected year: D.2 and
 * E.1 have no commitment at all and the chart shows nothing for them, whereas a 2024
 * selection on an indicator that does have a trajectory is `pending: false` with
 * `target: null`. The two are different sentences and the chart draws them differently.
 *
 * `country_allocation_pending` is the other one that matters. Asking for a country gets the
 * **programme** figure back, flagged, because IFDC has allocated none of the commitment
 * across the four countries — so a caller renders it as a reference line and never as that
 * country's own bar. Nothing prorates; see `docs/client-specs/2026-09-09-targets-ingest.md`.
 */
export interface TargetsResponse {
  indicator: ValuesIndicator;
  /** The year asked for, before the 2033 cap. */
  requested_year: number;
  /** The trajectory year actually read — capped at 2033, `null` before 2026. */
  year: number | null;
  capped: boolean;
  scope: { type: 'programme' | 'country'; id: string | null };
  country_allocation_pending: boolean;
  /** The selected year's row, or `null` when the trajectory does not reach that year. */
  target: TargetRow | null;
  /** The 2033 row — the end-of-programme ambition. */
  eop: TargetRow | null;
  /** All eight years, so a caller can state the ambition without a second request. */
  trajectory: TargetRow[];
  pending: boolean;
}

export type TargetsKey = readonly ['targets', string, ReportingLevel, number, Country];

export const fetchTargets: QueryFunction<TargetsResponse, TargetsKey> = ({
  queryKey: [, indicatorId, , year, country],
}) =>
  getJson<TargetsResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/targets${qs({
      year,
      country: country === 'ALL' ? null : country,
    })}`,
  );

/* -------------------------------------------------------------- §D2 hierarchy */

export type UnitType = 'admin' | 'watershed' | 'partnership';

/**
 * `simplify` — §D2. `z7` is the default and the only one inside the §J1 2 MB budget at
 * every level. `full` is not offered: it serialises the raw IFDC polygons (14 MB for the
 * 1048 communes) and the API rejects it with a 400.
 */
export type Simplify = 'z5' | 'z7' | 'z10';

export interface UnitProperties {
  id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  parent_id: string | null;
  country: string | null;
  area_km2: number | null;
  [key: string]: unknown;
}

export interface Feature<G = unknown, P = Record<string, unknown>> {
  type: 'Feature';
  id?: string | number;
  geometry: G | null;
  properties: P;
}

export interface FeatureCollection<G = unknown, P = Record<string, unknown>> {
  type: 'FeatureCollection';
  features: Feature<G, P>[];
}

export type UnitsFC = FeatureCollection<unknown, UnitProperties>;

/**
 * §D2 `GET /hierarchy/units`.
 *
 * `parent` is §F5.8's cascading filter: the id of the unit the reader has filtered to, whose
 * subtree — that unit **and** everything under it — is the whole answer. It composes with
 * `type`/`level` rather than replacing them, so `(admin, 2, parent=BF57)` is "the communes of
 * Djoro" and `(admin, 2, parent=BF5701)` is "that one commune". `null` is the whole
 * programme, and then the request URL is byte-for-byte what it was before the cascade.
 */
export const fetchUnits = (
  type: UnitType,
  level?: number | null,
  country: Country = 'ALL',
  simplify: Simplify = 'z7',
  parent: string | null = null,
) =>
  getJson<UnitsFC>(
    `/hierarchy/units${qs({
      type,
      level: level ?? null,
      country: country === 'ALL' ? null : country,
      simplify,
      parent,
    })}`,
  );

/**
 * §F5.8 `GET /hierarchy/options` — `{id, name}` rows for a `<select>`, no geometry.
 *
 * The cascade's four dropdowns would otherwise be filled from `/hierarchy/units`, which
 * answers the same question about Burkina Faso's communes in 141 kB of polygons instead of
 * 4.9 kB of names — four times over, as the reader walks the cascade.
 */
export interface UnitOption {
  id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  parent_id: string | null;
  country: string | null;
}

/**
 * `operational` — client review R6: *"the cascade dropdowns list only OPERATIONAL units — a
 * unit (or a descendant) carrying ≥1 located MELS activity or ≥1 approved MELS-sourced
 * indicator value."*
 *
 * Default **on**, because the cascade is the only caller: offering 1 048 communes for a
 * programme that reports from 67 of them is a list a reader cannot use, and the 981 others
 * lead to an empty map. `/hierarchy/units`, `/tiles` and `/search` do not take the flag —
 * the map still draws every boundary, which is the difference between "we do not work here"
 * and "this place does not exist".
 *
 * The one thing it will never do is answer with nothing: where the shortlist would be empty —
 * a stack between `ingest_ifdc` and its first MELS sync — the server returns the unfiltered
 * list and sets `X-Operational-Fallback: 1` (`apps/hierarchy/views.py::options`). So the rows
 * are always usable, and a caller that wants to *say* which of the two it got has a header to
 * read rather than a count to guess from.
 */
export const fetchUnitOptions = (
  type: UnitType,
  level: number | null = null,
  country: Country = 'ALL',
  parent: string | null = null,
  operational = true,
) =>
  getJson<UnitOption[]>(
    `/hierarchy/options${qs({
      type,
      level,
      country: country === 'ALL' ? null : country,
      parent,
      operational: operational ? 1 : null,
    })}`,
  );

export interface SiteProperties {
  id: string;
  name: string;
  site_type: string;
  status: string;
  [key: string]: unknown;
}

export const fetchSites = (country: Country = 'ALL', siteType?: string | null) =>
  getJson<FeatureCollection<unknown, SiteProperties>>(
    `/hierarchy/sites${qs({ country: country === 'ALL' ? null : country, type: siteType ?? null })}`,
  );

/* ------------------------------------------------------------- §D3 indicators */

export type Tier = 'contractual' | 'outcome' | 'other';

/** §D3 `GET /indicators` row. `code` and `full_name` are null for non-MEL indicators. */
export interface Indicator {
  id: string;
  code: string | null;
  name: string;
  full_name: string | null;
  unit: string;
  domain: string;
  tier: Tier;
  /**
   * How §E4 values a parent from its children. `'none'` is the third and it is not a third
   * roll-up rule: it says the series **has no roll-up** — a survey estimate is served at the
   * geographies it was estimated at and nowhere else, so an empty level is by design rather
   * than by an unreported year. The two surfaces that read it are the legend's empty state
   * and the analytics panel's; everything else treats all three the same.
   */
  aggregation: 'sum' | 'mean' | 'none';
  is_cumulative: boolean;
  /** Decimal places for display (§G3 / `fmtNum`); 0 = a count. */
  fmt: number;
  source: string;
  cadence: string | null;
  /**
   * §M3 disaggregation helper (`b21_female`, …) — a real indicator the dashboard charts
   * read, kept out of the picker. The endpoint already filters these out unless
   * `?include_hidden=1`, so a row reaching the SPA is normally `false`.
   */
  hidden: boolean;
  /**
   * §M3.5 — the indicator has **native** watershed-stratum values (MELS reports it per
   * `watershed_id`), so the Operational-hierarchy watershed radio may be enabled for it.
   * Not "the engine could area-weight one": §E4 can do that for anything.
   */
  has_watershed_values: boolean;
  /**
   * §D3 — the reporting levels this indicator has **stored** values at, in §F5.8 row order:
   * `[0, 1, 2]` for a seeded series, `[0, 'watershed']` for a 2025 baseline survey estimate,
   * `[]` for one holding nothing.
   *
   * The map's level has derived purely from the §F5.8 cascade since client review R5 — no
   * filter means regions — and the baseline is `aggregation: 'none'`, so it has no region
   * rows and no roll-up that could make any: every one of the 58 series painted an empty map
   * on the view a reader opens on. `lib/state.effectiveLevels` turns this into the set the
   * level snaps to, and it deliberately does **not** read it as gospel for a `sum`/`mean`
   * series, whose stored levels understate what §E4 will answer for.
   */
  levels: ReportingLevel[];
  /**
   * Which workbook sheet an `mb_*` baseline sub-indicator came off (`agroforestry`,
   * `crop_yield`, …) and that sheet's name in the interface language — `null` for every
   * indicator that is not one of the 58.
   *
   * The 2025 baseline is a catalogue of *sub-indicators*: eight agroforestry practices,
   * eleven soil practices, twenty crops, two halves of one gendered extension question. As
   * 58 flat picker rows they were unfindable; as ten labelled clusters they are a list.
   * Derived server-side from the id prefix — there is no column.
   */
  group_id: string | null;
  group_label: string | null;
  /**
   * The distinct provenances actually behind this indicator's approved values, sorted —
   * `['demo']`, `['mels']`, `['demo','mels']`, or `[]` when it has no values at all.
   *
   * Not the same field as `source` above, which is the §C1 catalogue's *declaration* of
   * where the indicator is meant to come from. The two disagree for `b21`/`c21`, which
   * declare `mels` and hold four seeded years plus two real ones — and the §F5.6 legend
   * foot is written from **this** one, so it stops labelling real MELS reporting as
   * simulated (M3 review I4).
   */
  sources: IndicatorSource[];
  /**
   * Whether the picker may let a reader map this indicator. Derived server-side from
   * `sources`: false when the indicator holds demo seed and **only** demo seed.
   *
   * A *display* flag, not a data one — the row is still in this payload, `/values`,
   * `/series` and `/export` all still answer for it, and nothing was deleted. The picker
   * greys it (`aria-disabled`, no fill/overlay/bivariate) because 25 of the 27 catalogue
   * indicators currently paint a confident choropleth over simulated numbers, and a client
   * cannot be expected to keep a `Demo` chip in mind while reading a map.
   *
   * An indicator with no values at all (`sources: []`) is **active**: it is not demo data,
   * it is a real indicator MELS has not published yet, and the time bar already says so.
   */
  active: boolean;
  /**
   * §D3: the programme years this indicator actually has approved values for. Since the
   * demo purge `b21`/`c21` hold 2025–26 only, so the §F5.10 time bar greys the rest —
   * an empty map on 2021 reads as a broken portal rather than as an unpublished year.
   * `[]` for an indicator with no values at all.
   */
  years: number[];
  /**
   * §D3: the year a cold load should **open** this indicator on — the latest year holding
   * at least half the best-reported year's stored rows — or `null` when it has no values.
   *
   * `years` was not enough to choose one. B.2.1's 2026 is a real year with real data (three
   * rows) and its 2025 is eighty, so "the newest year with data" opened the portal on a map
   * with a single painted polygon, which reads as broken rather than as a reporting year
   * that has barely begun. Every year in `years` is still selectable; this only says where
   * the slider starts (`state.applyDefaultYear`).
   */
  default_year: number | null;
}

/** A `sources` token (`apps.indicators.provenance`). `derived` is a value this platform
 * computed from a named input (M4: `spi`, SPI-3 at end-September from the CHIRPS archive) —
 * not `manual`, because nobody reported it, and not `other`, which is the word the API
 * reserves for a provenance it cannot name. `other` is a recorded but unrecognised
 * `source_ref` prefix — deliberately not folded into `manual`. */
export type IndicatorSource = 'mels' | 'demo' | 'manual' | 'derived' | 'other';

/** §D3 `GET /indicators[?lang=fr]` — see `fetchConfig` on why the language is a parameter. */
export const fetchIndicators: QueryFunction<Indicator[], IndicatorsKey> = ({
  queryKey: [, lang],
}) => getJson<Indicator[]>(`/indicators${langParam(lang)}`);

/**
 * The `/hierarchy/units` request a reporting level implies (§M3.5).
 *
 * One helper so the choropleth, the overlays and the legend can never disagree about
 * *which polygons* the current level means — `'watershed'` is the overlay hierarchy
 * (`type=watershed`, no admin level), everything else is `type=admin` at that level.
 */
export function unitsQueryFor(level: ReportingLevel): { type: UnitType; level: number | null } {
  return isOverlayLevel(level)
    ? { type: level, level: null }
    : { type: 'admin', level };
}

/**
 * §D3 `?mode=` — how a year is read (see `AppState.cumulative`).
 *
 * `annual` is the stored series: the value *for* that year. `cumulative` is Σ over every
 * programme year up to and including it, which is the figure MELS publishes on its own
 * dashboard. The server computes the running total from the same annual rows the map
 * paints, so the two readings can never disagree.
 */
export type ValueMode = 'annual' | 'cumulative';

export const ANNUAL: ValueMode = 'annual';
export const CUMULATIVE: ValueMode = 'cumulative';

/**
 * The mode to actually request for one indicator — the store's flag, narrowed by the
 * catalogue.
 *
 * `cumulative` is only meaningful for an `is_cumulative` series: the running total of a
 * rainfall anomaly or a soil-carbon stock is not a number. The server ignores the flag for
 * those anyway (and says so in the payload's `mode`), but narrowing it here is what keeps
 * the *query key* — and therefore the cache entry and the request URL — identical to the
 * plain annual one, so switching the checkbox never refetches an indicator it cannot
 * affect. `meta` is `null`/`undefined` while `['indicators']` is in flight; the answer is
 * `annual` then, and the query re-keys itself once the catalogue lands.
 */
export function modeFor(
  cumulative: boolean,
  meta: Pick<Indicator, 'is_cumulative'> | null | undefined,
): ValueMode {
  return cumulative && meta?.is_cumulative ? CUMULATIVE : ANNUAL;
}

/** The trimmed indicator header §D3 echoes back with a values payload. */
export type ValuesIndicator = Pick<
  Indicator,
  'id' | 'code' | 'name' | 'unit' | 'tier' | 'aggregation' | 'is_cumulative' | 'fmt'
>;

/**
 * §D3 `GET /indicators/{id}/values`. Units with no data are **omitted** from `values`
 * (Task 4 report §1), so the choropleth has to treat "absent" as no-data, not as zero.
 * `breaks_hint` is always null — §G1–G3 classify in the browser.
 */
export interface ValuesResponse {
  indicator: ValuesIndicator;
  year: number;
  /**
   * The reading actually served. Echoed back because the server may narrow it: asking for
   * `cumulative` on a non-cumulative indicator answers `annual`, and a caller must be able
   * to tell what the numbers mean rather than assume it got what it asked for.
   */
  mode: ValueMode;
  /**
   * `0|1|2`, `null` for a single overlay unit, `'programme'` (§F5.9) or `'watershed'`
   * (§M3.5 — the whole overlay level at once). Echoed back so a held-over payload from a
   * different level can be detected and dropped.
   */
  level: number | 'programme' | 'watershed' | null;
  values: {
    unit_id: string;
    value: number;
    /**
     * Provenance of the **stored** row for this exact cell, or `null` when there is none
     * (see `derived`). M3 review I4.
     */
    source: IndicatorSource | null;
    /**
     * The sample the estimate was computed on, for a survey-estimate indicator
     * (`aggregation: 'none'` — the MELS baseline). `null` for every other kind of value and
     * for every derived one: a rolled-up number has no sample behind it.
     *
     * It is on the wire because the choropleth cannot show it: 33.8 % of 3,580 households
     * and 41 % of 18 land in classes that look equally confident.
     */
    sample_n: number | null;
    /**
     * True when §E4 *computed* this number — rolled up from child units, or area-weighted
     * from a watershed's overlapping admin cells — rather than reading a stored value.
     * M3 review I5: a reported figure and an estimate of it are otherwise identical on the
     * wire.
     */
    derived: boolean;
  }[];
  breaks_hint: number[] | null;
}

/**
 * §F5.9's pseudo-unit: one scope-wide number above admin level 0, served by §E4's
 * `programme_value` (Σ of the level-0 values for a count indicator, their area-weighted
 * mean for a rate). It is both the `?level=`/`?scope=` token and the `unit_id` the answer
 * carries back — the scope has no `org_units` row to name.
 */
export const PROGRAMME_UNIT = 'programme';

/**
 * `parent` is §F5.8's filter, the numbers half of `fetchUnits`'s: the same subtree, so the
 * polygons and the values are the same set of units by construction. The server **filters**
 * §E4's whole-scope answer rather than re-aggregating it, so a unit's number is identical
 * scoped and unscoped — what changes is how the §G1 breaks spread across the visible ones.
 */
export const fetchValues = (
  indicatorId: string,
  level: ReportingLevel,
  year: number,
  country: Country = 'ALL',
  mode: ValueMode = ANNUAL,
  parent: string | null = null,
) =>
  getJson<ValuesResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/values${qs({
      level,
      year,
      country: country === 'ALL' ? null : country,
      // Omitted when annual — that is the server's default, and an unchanged URL keeps
      // every existing cache entry, permalink and log line exactly as it was.
      mode: mode === ANNUAL ? null : mode,
      parent,
    })}`,
  );

/**
 * §D3 `GET /indicators/{id}/series?unit_id=` — the six-year series behind §F5.7's spark.
 * Every programme year is present; a year with no data is `null`, and the chart draws the
 * gap rather than closing it (a straight line across a missing year is a fabricated trend).
 */
export interface SeriesResponse {
  unit: { id: string; name: string };
  /** The reading served — see `ValuesResponse.mode`. */
  mode: ValueMode;
  years: number[];
  values: (number | null)[];
}

/**
 * In `cumulative` the series is the running total: leading years with no data stay `null`,
 * and from the first value on every year carries the total to date (so a year that reported
 * nothing repeats the previous point rather than opening a gap — the total did not vanish).
 */
export const fetchSeries = (indicatorId: string, unitId: string, mode: ValueMode = ANNUAL) =>
  getJson<SeriesResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/series${qs({
      unit_id: unitId,
      mode: mode === ANNUAL ? null : mode,
    })}`,
  );

/**
 * §F5.9 `GET /indicators/{id}/values?level=programme[&country=]` — the dock popover's big
 * number, aggregated **server-side** so it can never disagree with the dashboard's KPI
 * tile: §E4 decides sum-vs-mean from `indicators.aggregation`, and re-deciding that in the
 * browser would be a second implementation of the programme's headline figures.
 *
 * `values` is `[]`, not `[{value: null}]`, when nothing in scope reported — the same
 * "absent ≠ zero" rule as every other §D3 values payload.
 */
export const fetchProgrammeValue = (
  indicatorId: string,
  year: number,
  country: Country = 'ALL',
  mode: ValueMode = ANNUAL,
) =>
  getJson<ValuesResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/values${qs({
      level: PROGRAMME_UNIT,
      year,
      country: country === 'ALL' ? null : country,
      mode: mode === ANNUAL ? null : mode,
    })}`,
  );

/**
 * §F5.9 `GET /indicators/{id}/series?scope=programme[&country=]` — the same number for
 * each of the six programme years, behind the popover's "Trend 2021–2026" spark. Year-
 * independent, so a time-bar tick re-reads it from cache (see `queryKeys.programmeSeries`)
 * and it is also where the ▲/▼ delta's `value(year − 1)` comes from (§E4).
 */
export const fetchProgrammeSeries = (
  indicatorId: string,
  country: Country = 'ALL',
  mode: ValueMode = ANNUAL,
) =>
  getJson<SeriesResponse>(
    `/indicators/${encodeURIComponent(indicatorId)}/series${qs({
      scope: PROGRAMME_UNIT,
      country: country === 'ALL' ? null : country,
      mode: mode === ANNUAL ? null : mode,
    })}`,
  );

/**
 * §D3 `GET /indicators/values/bulk?ids=…&level=&year=&country=` — up to 6 indicators in one
 * round trip, keyed indicator → unit. Backs §F5.7's "Other indicators" rows, which would
 * otherwise be six `/values` requests for six numbers.
 */
export interface BulkValuesResponse {
  year: number;
  level: number;
  indicators: Record<string, Record<string, number | null>>;
}

export const fetchBulk = (
  ids: string[],
  level: number,
  year: number,
  country: Country = 'ALL',
) =>
  getJson<BulkValuesResponse>(
    `/indicators/values/bulk${qs({
      ids: ids.join(','),
      level,
      year,
      country: country === 'ALL' ? null : country,
    })}`,
  );

/**
 * §D3 `GET /indicators/export` — a URL, not a fetch. The endpoint answers with a CSV
 * attachment, so the browser's own download path (`<a href download>`) is the right
 * client: no blob in memory, no XHR to keep alive, and the file lands with the server's
 * filename. `unit_id` exports the whole series for one unit (§F5.7's "Export CSV");
 * `level` + `year` exports the choropleth as a table.
 */
export const exportCsvUrl = (
  params: Record<string, string | number | null | undefined>,
): string => `${API_BASE}/indicators/export${qs(params)}`;

/* --------------------------------------------------------- §D2 unit detail */

/** §D2 `GET /hierarchy/units/{id}` — the unit, its parent chain and its extent. */
export interface UnitDetail {
  id: string;
  name: string;
  unit_type: string;
  admin_level: number | null;
  parent_id: string | null;
  country: string | null;
  pcode: string | null;
  area_km2: number | null;
  source: string | null;
  version: string | null;
  valid: boolean;
  centroid: [number, number] | null;
  /** Human level name — `Country | Region | Commune | Watershed | Partnership` (§F5.7). */
  level_label: string;
  country_name: string | null;
  /** Nearest first (§D2). */
  parents: { id: string; name: string; level: number | null }[];
  crosswalk: Record<string, string>;
  /** `[minx, miny, maxx, maxy]` of `geom_z7`; null for a unit with no geometry. */
  bbox: [number, number, number, number] | null;
  /**
   * NASA POWER climate normals for this unit — `docs/DATA_SOURCES.md` §4, wave 5.
   *
   * `null` when nothing covers the unit: POWER is harvested for admin-0 and admin-1 only,
   * an admin-2 unit inherits its region's row (`inherited: true`, `unit_name` naming which
   * region answered), and a watershed or partnership — whose `parent_id` chain never
   * reaches an admin unit — genuinely has none.
   */
  climate: UnitClimate | null;
}

/** §D2's `climate` block — see `apps/datasets/climate.py`. */
export interface UnitClimate {
  source: string;
  /** The unit the figures are *for*, which is not always the one that was asked for. */
  unit_id: string;
  unit_name: string;
  inherited: boolean;
  /** `[first, last]` year of the baseline — 1991–2020, the CHIRPS one. */
  period: [number, number];
  years: number;
  /** Mean 2 m air temperature, °C. */
  t2m_c: number | null;
  /** OLS slope of the annual means, °C per decade. */
  t2m_trend_c_decade: number | null;
  /** All-sky surface irradiance in POWER's own MJ/m²/day, and the same in kWh/m²/day. */
  solar_mj: number | null;
  solar_kwh: number | null;
  t2m_monthly: (number | null)[];
  solar_monthly: (number | null)[];
}

export const fetchUnitDetail = (id: string) =>
  getJson<UnitDetail>(`/hierarchy/units/${encodeURIComponent(id)}`);

export interface SearchHit {
  id: string;
  name: string;
  level_label: string;
  country_name: string;
}

/** `parent` keeps the §F5.10 box inside the §F5.8 filter — see `apps/hierarchy/views.py`. */
export const searchUnits = (q: string, country: Country = 'ALL', parent: string | null = null) =>
  getJson<SearchHit[]>(
    `/hierarchy/search${qs({ q, country: country === 'ALL' ? null : country, parent })}`,
  );

/* -------------------------------------------------------------- §D5 layers */

/**
 * §D5 layer kinds. Exactly one "where the pixels come from" key accompanies each:
 * `tile_template` (`cog_titiler`), `wms` (`wms_external`), `endpoint` (points/lines) —
 * or none at all, when the row is `unavailable` (Task 1 report: a `wms_external` whose
 * `config.proxy` is null or unknown reports itself rather than emitting a broken URL).
 */
export type LayerKind =
  | 'cog_titiler'
  | 'wms_external'
  | 'points_api'
  | 'lines_api'
  /**
   * Wave 4 (docs/DATA_SOURCES.md §3/§6): HydroLAKES shorelines, GRID3 settlement extents and
   * the water-point-density choropleth. Same `endpoint` payload as the other two vector
   * kinds — the addition is a geometry class, not a transport.
   */
  | 'polygons_api';

/** §F2 `Chip(kind)` — the §I1 classification a layer's access is derived from. */
export type Classification = 'open' | 'sensitive' | 'restricted';

/** §D5 legend entry for a categorical raster (`is_categorical`). */
export interface LayerLegendClass {
  label: string;
  color: string;
}

/**
 * §D5 `GET /layers` row.
 *
 * `access` is `access_for(role, classification)` computed **server-side** from the session
 * (§I1), which is why `queryKeys.layers(role)` carries the role: the body is role-dependent
 * and the response is `Cache-Control: private` + `Vary: Cookie` (Task 1 report).
 *
 * NONE-access rows are *omitted* for public/field/partner (§I3), so `access: 'none'` only
 * ever reaches analyst/manager — see `LayersPane`, which still lists such a row (disabled,
 * with the classification on it) because the pane must not depend on that server-side
 * filtering to be safe.
 */
export interface Layer {
  id: string;
  dataset_id: string;
  name: string;
  /**
   * The dataset abstract — the layer's second line, translated with `name` when `?lang=fr`
   * (§F12). Null for a layer with no dataset behind it; §F5.2 still falls back to
   * `{source} · {domain}` when it is absent, which is what it did before this key existed.
   */
  description: string | null;
  domain: string;
  kind: LayerKind;
  classification: Classification;
  is_provisional: boolean;
  is_categorical: boolean;
  /** §G ramp key into `/meta/config.ramps` (`brown`, `lc`, …); null for vector layers. */
  ramp: string | null;
  legend: LayerLegendClass[] | null;
  source_org: string;
  access: AccessLevel;
  /**
   * The CC-BY attribution string a licence obliges the portal to render wherever the pixels
   * are (docs/DATA_SOURCES.md implementation note 4). Null for a layer that needs none.
   */
  attribution: string | null;
  /**
   * §1a rule 4 — a live third-party layer whose pixels are never stored. The proxy already
   * refuses tiles outside the programme bbox; this flag is what tells the map to mask what
   * is left to the programme outline. It also says the layer is **not** analysis-grade.
   */
  display_only: boolean;
  /**
   * The layer's `/features` endpoint is **viewport-scoped**: fetch it with the map bounds as
   * `?bbox=` and re-fetch on pan. Set for the wave-4 feeds, which hold up to ~180 000
   * features against §J1's 2 MB response budget; false for the handful of `sites` rows the
   * mockup shipped with, which are cheaper to fetch once than to re-fetch per pan.
   */
  viewport?: boolean;
  /** `cog_titiler` — a Leaflet URL template; `{z}/{x}/{y}` are substituted verbatim. */
  tile_template?: string;
  /**
   * `wms_external` — the §B3 proxy URL (trailing slash) plus the GetMap parameters.
   *
   * `template` is present instead of `params` for a **RESTful WMTS** upstream (NASA GIBS):
   * the coordinates are in the path, so the client appends it to `url` and hands the result
   * to `L.tileLayer` rather than to `L.tileLayer.wms`. `max_zoom` is the deepest zoom the
   * upstream publishes — past it, a request is a 404 per tile, not a sharper picture.
   */
  wms?: {
    url: string;
    params: Record<string, string>;
    template?: string;
    max_zoom?: number;
  };
  /** `points_api` / `lines_api` / `polygons_api` — the absolute `/features` path. */
  endpoint?: string;
  /** True when the row cannot be drawn at all; `note` says why. */
  unavailable?: boolean;
  note?: string;
  /**
   * §D5 (M4 Task 1): the layer has a **published version with data behind it** — a COG on
   * disk for a raster kind, published features for a vector one. The map does not care (a
   * registered layer with no pixels simply draws nothing), but the §F6 tool pickers do:
   * offering §H3 a raster the engine will refuse turns a form into a failed job.
   */
  has_data?: boolean;
  /**
   * §D5 (M4 Task 6): the epoch year of a raster that declares one (`config.epoch`), else
   * `null`; absent for the non-raster kinds. **This is where §H5's two epoch selects come
   * from** — `epoch && is_categorical && has_data && access !== 'none'` — which is why
   * there is no `/analysis/epochs` route: one catalogue, already access-filtered.
   */
  epoch?: number | null;
  /**
   * S3's time slider. The **series** half of `epoch`: `epoch` says which year a row *is*,
   * this says which years a series row can step to and which layer id draws each — oldest
   * first, written by the harvester from what is actually on the volume, so the slider can
   * only ever offer a year the platform can draw.
   *
   * Empty on every row but a series one (`lc_annual`), and empty on that one until its
   * first harvest. Deliberately carries **no tile URL per year**: a client takes the year's
   * `layer` back to `/layers` and reads that row's own `tile_template`, which is the path
   * §I1 already gates.
   */
  epochs?: LayerEpoch[];
}

/** One year of a series layer's `epochs` (§D5). */
export interface LayerEpoch {
  year: number;
  /** The §D5 layer id that draws this year. */
  layer: string;
  dataset: string;
  version: string;
}

/** §D5 `GET /layers[?lang=fr]` — see `fetchConfig` on why the language is a parameter. */
/**
 * `GET /meta/programme-extent` — the §1a rule-4 display outline (docs/DATA_SOURCES.md).
 *
 * A simplified (0.02°, ~2 km) version of the same four-country dissolve the harvesters cut
 * rasters against, as bare `[lon, lat]` rings. **Not** the cutline: this geometry only ever
 * becomes a CSS `clip-path`, re-projected into pixels on every pan, so it is generalised to
 * ~1 500 points where the real cutline has ~41 000.
 *
 * `available: false` on a stack where `manage.py build_programme_extent` has not run. That
 * is a state, not an error: display-only layers then render bbox-gated but unmasked, which
 * is the same picture with a ragged edge rather than no picture at all.
 */
export interface ProgrammeExtent {
  available: boolean;
  version?: string;
  /** `[west, south, east, north]` in degrees. */
  bbox?: [number, number, number, number];
  /** Outer rings only, each a closed list of `[lon, lat]`. */
  rings?: [number, number][][];
  simplify_deg?: number;
}

export const fetchProgrammeExtent = () => getJson<ProgrammeExtent>('/meta/programme-extent');

export const fetchLayers: QueryFunction<Layer[], LayersKey> = ({ queryKey: [, , lang] }) =>
  getJson<Layer[]>(`/layers${langParam(lang)}`, { credentials: 'same-origin' });

export interface LayerFeatureProperties {
  id?: number | string;
  name?: string;
  site_type?: string;
  /** The middle of the three tooltip lines — water technology, lake type, reach length… */
  category?: string;
  status?: string;
  /**
   * Index into the layer's own `legend`, for a vector layer that is styled per feature
   * (`config.classify`): WPdx' functional status and the water-point-density classes. The
   * server sends a *class*, not a colour, so the swatch in §F5.6 and the mark on the map are
   * the same catalogue row read twice.
   */
  class?: number;
  [key: string]: unknown;
}

/**
 * §D5 `GET /layers/{id}/features?country=`. Layers with nothing published yet answer a
 * valid **empty** FeatureCollection carrying `note` (Task 1 report), so an empty map here
 * is a documented state and never a failed request.
 */
export type LayerFeatures = FeatureCollection<unknown, LayerFeatureProperties> & {
  note?: string;
  access?: AccessLevel;
  /**
   * How many features the filter selected **before** the server's row cap. It differs from
   * `features.length` exactly when the response was truncated, which is the state `note`
   * explains — the wave-4 feeds hold up to ~180 000 features and the endpoint never returns
   * more than a few thousand of them at once.
   */
  matched?: number;
};

/** `minx,miny,maxx,maxy` in EPSG:4326 — the map viewport, as `/features?bbox=` wants it. */
export type FeatureBbox = [number, number, number, number];

export const fetchLayerFeatures = (
  id: string,
  country: Country = 'ALL',
  bbox: FeatureBbox | null = null,
) =>
  getJson<LayerFeatures>(
    `/layers/${encodeURIComponent(id)}/features${qs({
      country: country === 'ALL' ? null : country,
      bbox: bbox ? bbox.map((n) => n.toFixed(4)).join(',') : null,
    })}`,
    { credentials: 'same-origin' },
  );

/* ------------------------------------------------------------- §F1 query keys */

/* ------------------------------------------------- §E8 / §H9 map export (M4 Task 9) */

/** §F5.10's Format select. `csv` is the one that never becomes a job (§E8). */
export type ExportFormat = 'png' | 'pdf' | 'tif' | 'gpkg' | 'csv';

/** §F5.10's Extent select. */
export type ExportExtent = 'view' | 'programme' | 'unit' | 'aoi';

/** Why a switched-on layer is not in the file — `apps.analysis.export.render.WITHHELD_*`. */
export type WithheldReason = 'access' | 'no_data' | 'not_drawable';

export interface ExportWithheld {
  layer: string;
  reason: WithheldReason;
  classification?: string;
}

/**
 * §H9's `state` — the §F1 store, as much of it as a picture of the map needs.
 *
 * `layersOn` is **advisory**: the server decides which of them this role may have at this
 * format and answers with `withheld`. See `apps.analysis.views.export_map`.
 */
export interface ExportState {
  indicator: string | null;
  level: ReportingLevel;
  year: number;
  country: Country;
  scope: string | null;
  layersOn: string[];
  overlays: { id: string; color?: string }[];
  biv: boolean;
  ramp: string | null;
  method: 'quantile' | 'equal';
  k: number;
  /**
   * The drawn ring in **`[lon, lat]`**, GeoJSON's order — which is what
   * `apps.analysis.geometry.ring_of` reads, both to frame `extent: 'aoi'` and to draw the
   * outline on the page. The store keeps Leaflet's `[lat, lon]`, so a caller converts with
   * `lib/analysis.aoiRing` (NOTES #630).
   */
  aoi: [number, number][] | null;
  unit_id: string | null;
  cumulative: boolean;
}

export interface ExportRequest {
  format: Exclude<ExportFormat, 'csv'>;
  extent: ExportExtent;
  /** Required for `extent: 'view'` — `[west, south, east, north]`. */
  bbox?: FeatureBbox;
  state: ExportState;
}

/** The §H9 result payload, once the job is done. */
export interface ExportResult {
  format: string;
  ext: string;
  extent: { kind: string; id: string | null; label: string };
  artifact_uri: string;
  bytes: number;
  detail: Record<string, unknown>;
  layers: { id: string; name: string; kind: string }[];
  withheld: ExportWithheld[];
  omitted_for_role: number;
  notes: string[];
}

/** §D6's answer to the submit, plus §H9's own `withheld` echo. */
export interface ExportSubmission {
  job_id: string;
  status?: 'queued' | 'done' | 'error';
  cached?: boolean;
  result?: ExportResult;
  error?: string;
  withheld: ExportWithheld[];
}

/** §D6 `GET /analysis/jobs/{id}` — the poll every M4 tool lives on. */
export interface AnalysisJobStatus<T = unknown> {
  status: 'queued' | 'running' | 'done' | 'error';
  progress: number;
  result?: T;
  error?: string;
  result_uri?: string;
}

/** §H9 `POST /export/map` — queue a picture (or a package) of the current map. */
export const submitExportMap = (body: ExportRequest) =>
  sendJson<ExportSubmission>('/export/map', body).then((r) => r as ExportSubmission);

/** §D6 `GET /analysis/jobs/{id}`. Not cached: the answer changes between two requests. */
export const fetchAnalysisJob = <T = unknown>(jobId: string) =>
  getJson<AnalysisJobStatus<T>>(`/analysis/jobs/${encodeURIComponent(jobId)}`, {
    credentials: 'same-origin',
  });

/**
 * §D6 `GET /analysis/results/{job_id}.{ext}` — a job's artefact, parsed as JSON.
 *
 * The one caller is the §E7 oversized-result spill (`lib/analysis.ts`): a result over 200 kB
 * is written whole to the job's artefact directory and the row keeps a summary plus this
 * link, so the panel has to follow it to get its rows back.
 *
 * `uri` is the server's own `result_uri`, which is already absolute *and* already carries
 * the `/api/v1` prefix `getJson` would add. Stripping it here rather than re-deriving the
 * path keeps the client from having to know how an artefact is addressed — and a `uri` from
 * anywhere else is passed through unchanged, where it will simply 404 rather than silently
 * become a different endpoint.
 *
 * `same-origin` credentials because the route re-checks §I3 on the job's own inputs: an
 * artefact is exactly as gated as the run that produced it.
 */
export const fetchAnalysisArtifact = <T = unknown>(uri: string) =>
  getJson<T>(uri.startsWith(`${API_BASE}/`) ? uri.slice(API_BASE.length) : uri, {
    credentials: 'same-origin',
  });

/**
 * §D3 `/indicators/export` as a URL — §E8's "CSV: immediate (no job)".
 *
 * A URL rather than a fetch, because the response is a `Content-Disposition: attachment`
 * stream and the browser is better at saving it than we are: the modal renders an ordinary
 * `<a href download>`, so the download survives a slow server, shows in the download
 * manager and needs no blob, no object URL and no revoke.
 */
export function indicatorsExportUrl(params: {
  indicator?: string | null;
  level?: number | null;
  unitId?: string | null;
  year?: number | null;
  country?: Country;
  parent?: string | null;
}): string {
  return `${API_BASE}/indicators/export${qs({
    indicator: params.indicator ?? null,
    unit_id: params.unitId ?? null,
    level: params.unitId ? null : (params.level ?? null),
    year: params.unitId ? null : (params.year ?? null),
    country: params.country && params.country !== 'ALL' ? params.country : null,
    parent: params.unitId ? null : (params.parent ?? null),
    format: 'csv',
  })}`;
}

export const queryKeys = {
  me: () => ['me'] as const,
  /**
   * The three **catalogue** keys carry the language: their bodies are translated
   * server-side (indicator names, layer names, domain labels), so the English and the
   * French answer are two different payloads and both are worth keeping. The default
   * argument means no call site has to pass it, and it comes from the same
   * `currentLanguage()` the fetcher uses — key and URL cannot drift apart.
   *
   * `units`, `sites` and `values` are **not** keyed by language and must not be: their
   * bodies are geometry and numbers, identical in both languages, and a language key there
   * would buy a second 2 MB copy of every admin level for a payload that did not change.
   * (`unit.level_label` is the one label the API still answers in English — Part 3 covers
   * the catalogue, not the hierarchy — see docs/NOTES.md.)
   */
  config: (lang: Language = currentLanguage()): ConfigKey => ['config', lang],
  /**
   * §F4. Language-keyed for the same reason `config` is: the donut's slice labels are
   * translated server-side, so the two languages are two payloads. `activityPoints` below
   * is **not**, because its body is coordinates and counts — identical in both.
   */
  overview: (country: Country, lang: Language = currentLanguage()): OverviewKey =>
    ['overview', country, lang],
  ticker: (lang: Language = currentLanguage()): TickerKey => ['ticker', lang],
  // `by` is in the key because the two groupings are two payloads over the same scope: the
  // unit answer is 65 communes, the watershed answer is 11 basins, and serving one for the
  // other would shade the splash from geometry it does not have.
  activityPoints: (country: Country, by: ActivityGrouping = 'unit') =>
    ['activityPoints', country, by] as const,
  /**
   * S2's pathway chart. **Language-keyed** — the pathway names are translated server-side —
   * and keyed by nothing else, which is the point: the chart recalculates for the map extent
   * and the selection out of this one cached payload, so panning never invalidates it.
   */
  pathways: (country: Country, lang: Language = currentLanguage()): PathwaysKey =>
    ['pathways', country, lang],
  /**
   * S2's target bars. Not language-keyed: the body is numbers and unit ids, and the one
   * piece of prose in it (`source`) is a citation of an IFDC document, not a translated
   * label — the same string in both languages.
   */
  targets: (
    ind: string,
    level: ReportingLevel,
    year: number,
    country: Country,
  ): TargetsKey => ['targets', ind, level, year, country],
  // `simplify` is part of the key, not just the request: two rows can ask for the same
  // type/level/country at different generalisations, and without it the second one would
  // be served the first one's cached geometry.
  //
  // `parent` (§F5.8) is in the key for the same reason and a sharper one: the scoped and
  // unscoped answers for `(admin, 2, BFA)` are *different sets of features*, and serving one
  // for the other would paint the whole country under a filter that says one region. It is
  // the last element and defaults to `null`, so every unscoped key is what it always was
  // plus a trailing `null` — one shape, and no call site outside this object to keep in step.
  units: (
    type: UnitType,
    level: number | null,
    country: Country,
    simplify: Simplify = 'z7',
    parent: string | null = null,
  ) => ['units', type, level, country, simplify, parent] as const,
  /**
   * §F5.8 cascade dropdowns — names only, so `simplify` has no meaning here.
   *
   * `operational` is part of the key (R6): the filtered and unfiltered answers are two
   * different lists for the same `type/level/country/parent`, and caching them as one would
   * serve whichever arrived first.
   */
  unitOptions: (
    type: UnitType,
    level: number | null,
    country: Country,
    parent: string | null = null,
    operational = true,
  ) => ['unitOptions', type, level, country, parent, operational] as const,
  sites: (country: Country) => ['sites', country] as const,
  search: (q: string, country: Country, parent: string | null = null) =>
    ['search', q, country, parent] as const,
  indicators: (lang: Language = currentLanguage()): IndicatorsKey => ['indicators', lang],
  // `mode` is part of every values/series key: the annual and the cumulative readings of
  // the same indicator-year are two different answers, and they are both worth caching —
  // a reader ticking the box on and off must not pay for a refetch each way.
  values: (
    ind: string,
    level: ReportingLevel,
    year: number,
    country: Country,
    mode: ValueMode = ANNUAL,
    parent: string | null = null,
  ) => ['values', ind, level, year, country, mode, parent] as const,
  /** §F5.7 spark — the series is year-independent, so the year is *not* in the key. */
  series: (ind: string, unit: string, mode: ValueMode = ANNUAL) =>
    ['series', ind, unit, mode] as const,
  /** §F5.9 dock popover — the one scope-wide number for a year. */
  programmeValue: (ind: string, year: number, country: Country, mode: ValueMode = ANNUAL) =>
    ['programmeValue', ind, year, country, mode] as const,
  /** §F5.9 popover spark. Year-independent like `series`, so a year tick is a cache hit. */
  programmeSeries: (ind: string, country: Country, mode: ValueMode = ANNUAL) =>
    ['programmeSeries', ind, country, mode] as const,
  /** §F5.7 "Other indicators". `ids` is joined so the key stays a flat, stable tuple. */
  bulk: (ids: string[], level: number, year: number, country: Country) =>
    ['bulk', ids.join(','), level, year, country] as const,
  /** §D2 unit detail — name, level label, parents and bbox for one unit. */
  unit: (id: string) => ['unit', id] as const,
  /**
   * §D5 `GET /layers` — **role-keyed**. The server computes `access` per §I1 and omits
   * NONE rows for public/field/partner (§I3), so two roles get two different bodies from
   * one URL. Without the role in the key the §F3 demo switcher would keep showing the
   * previous role's catalogue out of cache.
   */
  layers: (role: Role, lang: Language = currentLanguage()): LayersKey => ['layers', role, lang],
  /**
   * §1a rule 4 display outline. Not keyed by role or language: it is geometry, identical
   * for every reader, and it changes only when a boundary version is approved.
   */
  programmeExtent: () => ['programmeExtent'] as const,
  /** §D5 `GET /layers/{id}/features?country=` — the country is a request parameter. */
  /**
   * `bbox` is part of the key because it is part of the request (§D5 `?bbox=`). It is
   * serialised rather than kept as an array so two equal boxes are one cache entry —
   * react-query hashes the key structurally, and `VectorLayers.viewportBbox` snaps the
   * numbers so a small pan produces the same string.
   */
  layerFeatures: (id: string, country: Country, bbox: FeatureBbox | null = null) =>
    ['layerFeatures', id, country, bbox ? bbox.join(',') : null] as const,
  /**
   * §D6 `GET /analysis/results/{job_id}.{ext}` — a finished run's artefact (M4).
   *
   * Keyed by the **job**, not by the URL, and by nothing else: a job's artefact is written
   * once and never rewritten, so two readers of the same result share one fetch and a
   * re-registered result layer costs none at all. Not role-keyed either — §I3 decides
   * whether the *download* is permitted, and a refusal is an error rather than a second
   * body worth caching.
   */
  analysisResult: (jobId: string, ext = 'geojson') => ['analysisResult', jobId, ext] as const,
  /**
   * S3's Analytics & Insights panel — `GET /analysis/insights` (`apps/analysis/insights.py`).
   *
   * Every input the route takes is in the key, language included: the class labels, the
   * country names and the layer name are translated server-side, so the English and the
   * French answers are two payloads. Not role-keyed — §I3 refuses a layer a role may not
   * read rather than serving a second, thinner body of it.
   */
  insights: (
    layer: string,
    unit: string | null,
    country: Country,
    level: ReportingLevel,
    top: number,
    lang: Language = currentLanguage(),
  ) => ['insights', layer, unit, country, level, top, lang] as const,
};

/** §F1: "staleTime 5 min". One client for the whole app, created in App.tsx. */
export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 5 * 60 * 1000,
        gcTime: 30 * 60 * 1000,
        refetchOnWindowFocus: false,
        retry: (failureCount, error) => {
          // A 4xx is the caller's fault - never retry it.
          if (error instanceof ApiError && error.status >= 400 && error.status < 500) return false;
          return failureCount < 2;
        },
      },
    },
  });
}
